import PropTypes from "prop-types";
import React, { useRef } from "react";
import styled from "styled-components";
import { Form } from "react-final-form";
import {
  body1,
  Button,
  ButtonStyles,
  Card,
  CardStyles,
  FontColors,
  InputRow,
  overline,
} from "yuka";

import { FinalFormField } from "../hdYuka";

import AxiosInstance from "../api/AxiosInstance";
import { useDispatch, ACTIONS } from "../routes/StateProvider";

import { PaddedForm } from "./StyledComponents";
import LoadingSpinner from "../utils/LoadingSpinner";
import { API_ENDPOINTS } from "../api/constants";

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledInputContainer = styled.div`
  margin: 24px 0px;
`;

const CurrentEmailSpan = styled.span`
  ${overline}
  ${FontColors.theme50}
  margin-right: 120px;
`;

const UserEmailSpan = styled.span`
  ${body1}
  ${FontColors.theme80}
`;

const EditEmailForm = props => {
  const dispatch = useDispatch();
  const newEmailRef = useRef();
  const commentsRef = useRef();

  const validateEmail = value => (value ? undefined : "Required");

  const onSubmit = (values, form) => {
    return AxiosInstance.post(API_ENDPOINTS.EMAIL_CHANGE_REQUEST(), JSON.stringify(values))
      .then(() => {
        dispatch({
          type: ACTIONS.addToast,
          message:
            "Email change request submitted. Our team will review it and follow up by email.",
        });

        newEmailRef.current?.blur();
        commentsRef.current?.blur();
        form.restart();
      })
      .catch(error => {
        const data = error?.response?.data;
        let errorMsg = "Unknown error occurred";

        newEmailRef.current?.blur();
        commentsRef.current?.blur();

        if (data?.new_email) {
          errorMsg = Array.isArray(data.new_email) ? data.new_email[0] : data.new_email;
        }

        return { new_email: errorMsg };
      });
  };

  return (
    <Card
      cardStyle={CardStyles.SECTIONED}
      title="Email"
      tagline="Request an email address change. Our team will review your request and follow up by email once it's processed."
    >
      <Form onSubmit={onSubmit}>
        {({ handleSubmit, submitting, dirty }) => (
          <React.Fragment>
            {submitting && <LoadingSpinner />}
            <PaddedForm onSubmit={handleSubmit}>
              <div>
                <CurrentEmailSpan>Your current email</CurrentEmailSpan>
                <UserEmailSpan>{props.user?.email ?? "Email not found!"}</UserEmailSpan>
              </div>
              <StyledInputContainer>
                <InputRow>
                  <FinalFormField
                    id="new_email"
                    type="email"
                    name="new_email"
                    placeholder="Email"
                    label="New Email Address"
                    validate={validateEmail}
                    displayIndicator={false}
                    ref={newEmailRef}
                  />
                </InputRow>
                <InputRow>
                  <FinalFormField
                    id="comments"
                    type="text"
                    name="comments"
                    placeholder="Optional context for our support team"
                    label="Comments (optional)"
                    displayIndicator={false}
                    ref={commentsRef}
                  />
                </InputRow>
              </StyledInputContainer>
              <StyledButtonContainer>
                <Button
                  buttonStyle={ButtonStyles.CTA}
                  disabled={submitting || !dirty}
                  type="submit"
                >
                  Submit Request
                </Button>
              </StyledButtonContainer>
            </PaddedForm>
          </React.Fragment>
        )}
      </Form>
    </Card>
  );
};

EditEmailForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
  }).isRequired,
};

export default EditEmailForm;
